import React, { useState, useEffect } from 'react';
import { backgroundTaskService } from '../services/backgroundTaskService.js';

const BackgroundTaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const userTasks = await backgroundTaskService.getUserTasks();
      setTasks(userTasks);
      setError(null);
    } catch (err) {
      setError('Failed to load tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelTask = async (taskId) => {
    try {
      await backgroundTaskService.cancelTask(taskId);
      await loadTasks(); // Refresh the list
    } catch (err) {
      setError('Failed to cancel task: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'website_analysis': return 'Website Analysis';
      case 'email_verification': return 'Email Verification';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Background Tasks</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading tasks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Background Tasks</h3>
        <button
          onClick={loadTasks}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No background tasks found
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <div key={task.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <span className="font-medium">{getTaskTypeLabel(task.type)}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => cancelTask(task.id)}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Cancel
                    </button>
                  )}
                  <span className="text-sm text-gray-500">
                    {formatDate(task.created_at)}
                  </span>
                </div>
              </div>

              {task.progress !== undefined && task.progress > 0 && (
                <div className="mb-2">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progress</span>
                    <span>{task.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${task.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {task.error && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  <strong>Error:</strong> {task.error}
                </div>
              )}

              {task.result && (
                <div className="mt-2">
                  <details className="cursor-pointer">
                    <summary className="text-sm font-medium text-blue-600 hover:text-blue-800">
                      View Results
                    </summary>
                    <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                      {task.type === 'website_analysis' && task.result.results && (
                        <div>
                          <p className="font-medium mb-2">
                            Analyzed {task.result.results.length} websites
                          </p>
                          <div className="space-y-2">
                            {task.result.results.slice(0, 3).map((result, index) => (
                              <div key={index} className="border-l-2 border-blue-300 pl-2">
                                <div className="font-medium">{result.website}</div>
                                <div className="text-gray-600">
                                  Status: {result.status}
                                </div>
                              </div>
                            ))}
                            {task.result.results.length > 3 && (
                              <div className="text-gray-500">
                                ... and {task.result.results.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {task.type === 'email_verification' && task.result.results && (
                        <div>
                          <p className="font-medium mb-2">
                            Verified {task.result.results.length} emails
                          </p>
                          <div className="space-y-2">
                            {task.result.results.slice(0, 3).map((result, index) => (
                              <div key={index} className="border-l-2 border-green-300 pl-2">
                                <div className="font-medium">{result.email}</div>
                                <div className="text-gray-600">
                                  Status: {result.status}
                                </div>
                              </div>
                            ))}
                            {task.result.results.length > 3 && (
                              <div className="text-gray-500">
                                ... and {task.result.results.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              <div className="mt-2 text-xs text-gray-500">
                Task ID: {task.id}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BackgroundTaskManager;